import { useEffect } from 'react';
import { useQuestDetection, QuestTriggers } from '@/hooks/onboarding/useQuestDetection';
import { QUEST_STEP_IDS } from '@/types/quest-types';

interface QuestCompletionTriggerProps {
  stepId: number;
  action: string;
  triggerOnMount?: boolean;
  triggerDelay?: number;
}

/**
 * Component that triggers quest completion when mounted or when specific actions occur
 * Can be placed anywhere in the component tree to detect quest completion
 */
export const QuestCompletionTrigger: React.FC<QuestCompletionTriggerProps> = ({
  stepId,
  action,
  triggerOnMount = false,
  triggerDelay = 1000
}) => {
  const { triggerQuestCompletion, currentQuestStep, isQuestActive, isOnCorrectPage } = useQuestDetection();

  useEffect(() => {
    if (!isQuestActive || !currentQuestStep || currentQuestStep.id !== stepId) {
      return;
    }

    if (triggerOnMount && isOnCorrectPage) {
      const timer = setTimeout(() => {
        console.log(`[QuestCompletionTrigger] Auto-triggering quest completion for step ${stepId}: ${action}`);
        triggerQuestCompletion(stepId, action);
      }, triggerDelay);

      return () => clearTimeout(timer);
    }
  }, [stepId, action, triggerOnMount, triggerDelay, isQuestActive, currentQuestStep, isOnCorrectPage, triggerQuestCompletion]);

  // This component doesn't render anything
  return null;
};

/**
 * Hook to manually trigger quest completion from event handlers
 */
export const useQuestCompletionTrigger = () => {
  const { triggerQuestCompletion, currentQuestStep, isQuestActive } = useQuestDetection();

  const triggerIfCurrentStep = (stepId: number, action: string) => {
    if (isQuestActive && currentQuestStep?.id === stepId) {
      console.log(`[useQuestCompletionTrigger] Manually triggering quest completion for step ${stepId}: ${action}`);
      triggerQuestCompletion(stepId, action);
    }
  };

  return {
    triggerIfCurrentStep,
    // Convenience methods for each quest step
    triggerPageCreated: () => triggerIfCurrentStep(QUEST_STEP_IDS.CREATE_PAGE, 'page_created'),
    triggerResearchGenerated: () => triggerIfCurrentStep(QUEST_STEP_IDS.GENERATE_RESEARCH, 'research_generated'),
    triggerImageGenerated: () => triggerIfCurrentStep(QUEST_STEP_IDS.GENERATE_IMAGE, 'image_generated'),
    triggerProjectCreated: () => triggerIfCurrentStep(QUEST_STEP_IDS.CREATE_PROJECT, 'project_created'),
    triggerTaskAdded: () => triggerIfCurrentStep(QUEST_STEP_IDS.ADD_TASK, 'task_added'),
    triggerPageImported: () => triggerIfCurrentStep(QUEST_STEP_IDS.IMPORT_PAGE, 'page_imported'),
    triggerResearchImported: () => triggerIfCurrentStep(QUEST_STEP_IDS.IMPORT_RESEARCH, 'research_imported'),
    triggerTeamMemberAdded: () => triggerIfCurrentStep(QUEST_STEP_IDS.ADD_TEAM_MEMBER, 'team_member_added'),
    isQuestActive,
    currentQuestStep
  };
};